import * as React from 'react';
import type { Invoice } from 'contracts/tracking-v2/invoices';
import { useTripStore } from 'pages/tracking-v2/trip-details-page/hooks';
import {
  getInvoiceSupplierCoordinates,
  getInvoiceCompanyCoordinates,
} from 'pages/tracking-v2/utils/invoice-map-utils';
import {
  getUniqueGeolocations,
} from 'pages/tracking-v2/utils/geolication-utils';

const MAX_WAYPOINTS = 24;

export const useTripMapHelper = () => {
  const { trip } = useTripStore();

  const [originAndDestinationDirections, setOriginAndDestinationDirections]
    = React.useState<google.maps.DirectionsResult | null>(null);
  const [truckDirections, setTruckDirections]
    = React.useState<google.maps.DirectionsResult | null>(null);

  const originCoordinates = React.useMemo((): google.maps.LatLngLiteral | string | undefined => {
    const supplier = trip?.supplier;

    return supplier
      ? getInvoiceSupplierCoordinates(supplier)
      : undefined;
  }, [
    trip?.supplier,
  ]);

  const destinationCoordinatesArray = React.useMemo((): Array<google.maps.LatLngLiteral | string> => {
    if (!trip?.invoices?.length) return [];

    return trip.invoices.map(invoice => getInvoiceCompanyCoordinates(invoice.company));
  }, [trip?.invoices]);

  const originAndDestinationDirectionOptions = React.useMemo((): google.maps.DirectionsRequest | undefined => {
    if (!originCoordinates || !destinationCoordinatesArray.length) {
      return undefined;
    }

    const lastDestination = destinationCoordinatesArray[destinationCoordinatesArray.length - 1];

    return {
      origin: originCoordinates,
      destination: lastDestination,
      waypoints: destinationCoordinatesArray.slice(0, -1).map((coordinates) => ({
        location: coordinates,
        stopover: true,
      })),
      travelMode: 'DRIVING' as google.maps.TravelMode,
      optimizeWaypoints: false,
      provideRouteAlternatives: false,
    };
  }, [
    originCoordinates,
    destinationCoordinatesArray,
  ]);

  const handleOriginAndDestinationDirectionsServiceResponse = React.useCallback((
    result: google.maps.DirectionsResult | null,
    status: google.maps.DirectionsStatus,
  ) => {
    if (status !== 'OK' || !result) return;

    setOriginAndDestinationDirections(result);
  }, []);

  const originPosition = React.useMemo(() => {
    const routeLegs = originAndDestinationDirections?.routes[0]?.legs;
    return routeLegs?.[0]?.start_location;
  }, [originAndDestinationDirections]);

  const destinationPositions = React.useMemo(() => {
    const routeLegs = originAndDestinationDirections?.routes[0]?.legs || [];
    return routeLegs.map(leg => leg.end_location);
  }, [originAndDestinationDirections]);

  const truckCoordinatesArray = React.useMemo(() => {
    const geolocations: Invoice['geolocation'] = [];

    trip?.invoices?.forEach(invoice => {
      geolocations.push(...invoice?.geolocation);
    });

    return getUniqueGeolocations({
      geolocations,
      maxGeolocations: MAX_WAYPOINTS,
    });
  }, [trip?.invoices]);

  const lastTruckPosition = React.useMemo(() => {
    return truckCoordinatesArray[truckCoordinatesArray.length - 1];
  }, [truckCoordinatesArray]);

  const truckDirectionOptions = React.useMemo((): google.maps.DirectionsRequest | undefined => {
    if (!truckCoordinatesArray.length) return undefined;

    return {
      origin: truckCoordinatesArray[0],
      destination: truckCoordinatesArray[truckCoordinatesArray.length - 1],
      waypoints: truckCoordinatesArray.slice(1, -1).map(coordinates => ({
        location: coordinates,
        stopover: false,
      })),
      travelMode: 'DRIVING' as google.maps.TravelMode,
      optimizeWaypoints: false,
      provideRouteAlternatives: false,
    };
  }, [
    truckCoordinatesArray,
  ]);

  const handleTruckDirectionsServiceResponse = React.useCallback((
    result: google.maps.DirectionsResult | null,
    status: google.maps.DirectionsStatus,
  ) => {
    if (status !== 'OK' || !result) return;

    setTruckDirections(result);
  }, []);

  return {
    originAndDestinationDirectionOptions,
    handleOriginAndDestinationDirectionsServiceResponse,
    originAndDestinationDirections,
    originPosition,
    destinationPositions,
    truckDirections,
    truckDirectionOptions,
    handleTruckDirectionsServiceResponse,
    lastTruckPosition,
    mapDataIsReady: !!trip,
    invoices: trip?.invoices || [],
  };
};